// Absences CRUD operations using direct fetch (bypassing Supabase client)

/// <reference lib="dom" />

import { handleApiCall } from './errors'
import { getAccessToken } from './auth-token'
import { getSchoolContext } from './school-context'
import type {
  Absence,
  AbsenceWithRelations,
  LegacyCreateAbsenceRequest as CreateAbsenceRequest,
  UpdateAbsenceRequest,
  AbsenceFilters,
  RecordAbsenceResponse,
} from '@timetable/types'

// Get environment variables
const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}


// Get all absences with filters
export async function getAbsencesDirect(filters: AbsenceFilters = {}): Promise<AbsenceWithRelations[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*,teacher:teachers(*),assignments(*,absent_teacher:teachers!assignments_absent_teacher_id_fkey(*),substitute_teacher:teachers!assignments_substitute_teacher_id_fkey(*),class_section:class_sections(*,class:classes(*)),period:schedule_periods(*),subject:subjects(*))')
    
    // Apply school context filter (unless Super Admin without context)
    const schoolContext = getSchoolContext()
    const schoolId = filters.school_id || schoolContext
    
    if (schoolId) {
      params.append('school_id', `eq.${schoolId}`)
    }
    if (filters.teacher_id) {
      params.append('teacher_id', `eq.${filters.teacher_id}`)
    }
    if (filters.date_from) {
      params.append('date', `gte.${filters.date_from}`)
    }
    if (filters.date_to) {
      params.append('date', `lte.${filters.date_to}`)
    }
    if (filters.absence_type) {
      params.append('absence_type', `eq.${filters.absence_type}`)
    }
    params.append('order', 'date.desc')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/absences?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch absences')
    }

    return await response.json()
  })
}

// Get a single absence by ID
export async function getAbsenceByIdDirect(id: string): Promise<AbsenceWithRelations> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*,teacher:teachers(*),assignments(*,absent_teacher:teachers!assignments_absent_teacher_id_fkey(*),substitute_teacher:teachers!assignments_substitute_teacher_id_fkey(*),class_section:class_sections(*,class:classes(*)),period:schedule_periods(*),subject:subjects(*))')
    params.append('id', `eq.${id}`)

    const response = await fetch(`${SUPABASE_URL}/rest/v1/absences?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/vnd.pgrst.object+json',
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Absence not found')
    }

    return await response.json()
  })
}

// Create a new absence
export async function createAbsenceDirect(absence: CreateAbsenceRequest): Promise<Absence> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/absences`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(absence),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to create absence')
    }

    const data: any[] = await response.json()
    return data[0]
  })
}

// Record absence and trigger assignment engine
export async function recordAbsenceDirect(
  absence: CreateAbsenceRequest
): Promise<RecordAbsenceResponse> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Create the absence record
    const createdAbsence = await createAbsenceDirect(absence)

    // Trigger the assignment engine edge function
    try {
      const functionResponse = await fetch(`${SUPABASE_URL}/functions/v1/assign-substitutes`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          absence_id: createdAbsence.id,
        }),
      })

      if (!functionResponse.ok) {
        console.error('Failed to trigger assignment engine:', await functionResponse.text())
      }
    } catch (err) {
      console.error('Error triggering assignment engine:', err)
    }

    // Fetch the complete absence with assignments
    const absenceWithRelations = await getAbsenceByIdDirect(createdAbsence.id)

    return {
      absence: absenceWithRelations,
      assignments: absenceWithRelations.assignments,
    }
  })
}

// Update an absence
export async function updateAbsenceDirect(
  id: string,
  updates: UpdateAbsenceRequest
): Promise<Absence> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/absences?id=eq.${id}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(updates),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to update absence')
    }

    const data: any[] = await response.json()
    if (!data[0]) throw new Error('Absence not found')
    
    return data[0]
  })
}

// Delete an absence
export async function deleteAbsenceDirect(id: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/absences?id=eq.${id}`, {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to delete absence')
    }
  })
}
